"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateFilterHealthFromPressureDelta = exports.getReplacementDeltaForMerv = exports.calculateHVACPeakLoadPressureDelta = exports.parseHVACPacketData = exports.OPERATING_POINT_TOLERANCE = exports.STEADY_STATE_TOLERANCE = void 0;
// Time constants
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
// Below this the blower is off; anything left is CT noise.
const MIN_RUNNING_CT_AMPS = 0.5;
// Blower start/stop ramps show up as single CT samples well away from the running current
// (e.g. `2.55 → 3.29 → 1.68 → 0` on a SpacePak shutdown, `0 → 3.34 → 2.26` on start). Those
// samples pair with ramp-phase pressures that are nothing like steady-state TESP, and "top-N
// by CT" used to pick exactly them as the peak-load window. A sample only counts as
// steady-state when the CT samples on BOTH sides of it (each within
// STEADY_NEIGHBOR_MAX_GAP_SECONDS) agree with it to within STEADY_STATE_TOLERANCE.
exports.STEADY_STATE_TOLERANCE = 0.08;
const STEADY_NEIGHBOR_MAX_GAP_SECONDS = 90; // CT cadence is 30 s; tolerate two dropped packets
// Static pressure only compares like-for-like at the same blower operating point. Samples
// must sit within this band of the reference current (the baseline's recorded CT, or — for
// baselines captured before that was stored — the modal running current of the window).
// Fan laws: ΔP ∝ CFM², power ∝ CFM³, so ±8 % on current is roughly ±5 % on static pressure,
// inside the noise of a single reading.
exports.OPERATING_POINT_TOLERANCE = 0.08;
const MIN_STEADY_SAMPLES = 3;
// Most-recent steady, in-band samples to median over (30 × 30 s ≈ 15 min of blower time).
const STABLE_WINDOW_SIZE = 30;
// An R/S packet further than this from the CT sample is not "the same moment".
// R/S cadence is 10 s, so a healthy stream always has one within 5 s.
const MAX_PAIRING_GAP_SECONDS = 20;
// Pressure threshold constants (in inches of water)
const DEFAULT_TARGET_STATIC_PRESSURE = 0.5;
const DEFAULT_STARTING_STATIC_PRESSURE = 0.05;
// Max physical TESP across residential air handlers; anything above is a sensor fault.
const MAX_VALID_TESP = 3.0;
// Per-side pressure sanity bound (|return| or |supply|) — individual readings > this
// are discarded before median computation.
const MAX_VALID_PER_SIDE_PRESSURE = 2.0;
// Replacement delta (inH2O above clean baseline) defaults by MERV band.
const DELTA_MERV_LOW = 0.2; // MERV 6–8
const DELTA_MERV_MID = 0.25; // MERV 9–12 and fallback
const DELTA_MERV_HIGH = 0.3; // MERV 13+
// Health percentage constants
const MAX_HEALTH_PERCENTAGE = 100;
const MIN_HEALTH_PERCENTAGE = 0;
function parseHVACPacketData(event) {
    try {
        let values;
        if (typeof event.values === 'string') {
            try {
                values = JSON.parse(event.values);
            }
            catch (parseError) {
                console.warn(`Failed to parse values JSON for event ${event.id}:`, parseError);
                return null;
            }
        }
        else {
            values = event.values;
        }
        if (!values || typeof values !== 'object') {
            return null;
        }
        // find CT values (ct1/ct2) combined with other data
        if (values.ct1 !== undefined || values.ct2 !== undefined) {
            return {
                timestamp: event.timestamp,
                packetType: 'CT',
                ct1: values.ct1 != null ? Number(values.ct1) : undefined,
                ct2: values.ct2 != null ? Number(values.ct2) : undefined,
                temperature: values.temperature != null ? Number(values.temperature) : undefined,
                humidity: values.humidity != null ? Number(values.humidity) : undefined,
                pressure: values.pressure != null ? Number(values.pressure) : undefined,
                flow: values.flow != null ? Number(values.flow) : undefined,
            };
        }
        if (!values.packet_type) {
            return null;
        }
        const packetType = values.packet_type;
        const baseData = {
            timestamp: event.timestamp,
            packetType,
        };
        switch (packetType) {
            case 'R':
                return {
                    ...baseData,
                    temperature: values.temperature != null ? Number(values.temperature) : undefined,
                    humidity: values.humidity != null ? Number(values.humidity) : undefined,
                    pressure: values.pressure != null ? Number(values.pressure) : undefined,
                };
            case 'S':
                return {
                    ...baseData,
                    temperature: values.temperature != null ? Number(values.temperature) : undefined,
                    humidity: values.humidity != null ? Number(values.humidity) : undefined,
                    pressure: values.pressure != null ? Number(values.pressure) : undefined,
                    flow: values.flow != null ? Number(values.flow) : undefined,
                };
            case 'D':
                return {
                    ...baseData,
                    temperature: values.temperature != null ? Number(values.temperature) : undefined,
                    humidity: values.humidity != null ? Number(values.humidity) : undefined,
                };
            default:
                return null;
        }
    }
    catch (error) {
        console.error('Error parsing HVAC packet data:', error);
        return null;
    }
}
exports.parseHVACPacketData = parseHVACPacketData;
/** Blower current for a CT packet. Either leg may be the wired one, so take the larger. */
const ctAmps = (packet) => Math.max(packet.ct1 ?? 0, packet.ct2 ?? 0);
const withinBand = (value, reference, tolerance) => Math.abs(value - reference) <= reference * tolerance;
const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
};
/**
 * Closest packet to `target` by timestamp in a timestamp-sorted array, or null when the
 * nearest one is more than MAX_PAIRING_GAP_SECONDS away. Binary search — a 24 h window of
 * 10 s packets is ~9k entries per side, and this runs once per CT sample.
 */
function findClosestPacket(sorted, target) {
    if (sorted.length === 0)
        return null;
    let lo = 0;
    let hi = sorted.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (sorted[mid].timestamp < target)
            lo = mid + 1;
        else
            hi = mid;
    }
    let best = sorted[lo];
    if (lo > 0 && Math.abs(sorted[lo - 1].timestamp - target) < Math.abs(best.timestamp - target)) {
        best = sorted[lo - 1];
    }
    return Math.abs(best.timestamp - target) <= MAX_PAIRING_GAP_SECONDS ? best : null;
}
/**
 * Steady-state TESP for the last 24 h of air-handler packets.
 *
 * 1. Keep only CT samples whose neighbours on both sides agree with them (drops the
 *    start/stop ramp transients a blower emits every cycle).
 * 2. Keep only samples at the operating point — `options.referenceCT` when the baseline
 *    recorded one, else the window's modal running current.
 * 3. Pair the most recent STABLE_WINDOW_SIZE of those with their R/S packets from the same
 *    moment, discard sensor-fault readings, and take the median TESP.
 *
 * Returns null when the blower did not produce a comparable steady run in the window; the
 * caller skips the hour rather than grading the filter on incomparable pressure.
 */
function calculateHVACPeakLoadPressureDelta(events, currentTime = new Date(), options = {}) {
    const twentyFourHoursAgo = new Date(currentTime.getTime() - MILLISECONDS_PER_DAY);
    const lookbackTimestamp = Math.floor(twentyFourHoursAgo.getTime() / 1000);
    const recentEvents = events.filter((event) => event.timestamp >= lookbackTimestamp);
    const parsedPackets = recentEvents
        .map(parseHVACPacketData)
        .filter((packet) => packet !== null);
    const nullCount = recentEvents.length - parsedPackets.length;
    const packetTypeCounts = {};
    parsedPackets.forEach((p) => {
        packetTypeCounts[p.packetType] = (packetTypeCounts[p.packetType] || 0) + 1;
    });
    console.log(`Processing ${parsedPackets.length} HVAC packets from last 24 hours (${nullCount} unparseable, types: ${JSON.stringify(packetTypeCounts)})`);
    const ctPackets = parsedPackets
        .filter((packet) => packet.packetType === 'CT')
        .sort((a, b) => a.timestamp - b.timestamp);
    if (ctPackets.length === 0) {
        console.warn('No CT packets found in the last 24 hours');
        if (parsedPackets.length > 0) {
            console.warn(`Packet types found: ${JSON.stringify(packetTypeCounts)}`);
        }
        return null;
    }
    // Step 1 — steady-state samples only. The first and last CT packet of the window have
    // no neighbour on one side and are dropped; they contribute nothing a 24 h window misses.
    const steadyPackets = [];
    for (let i = 1; i < ctPackets.length - 1; i++) {
        const current = ctPackets[i];
        const amps = ctAmps(current);
        if (amps <= MIN_RUNNING_CT_AMPS)
            continue;
        const prev = ctPackets[i - 1];
        const next = ctPackets[i + 1];
        if (current.timestamp - prev.timestamp > STEADY_NEIGHBOR_MAX_GAP_SECONDS ||
            next.timestamp - current.timestamp > STEADY_NEIGHBOR_MAX_GAP_SECONDS) {
            continue;
        }
        if (!withinBand(ctAmps(prev), amps, exports.STEADY_STATE_TOLERANCE) ||
            !withinBand(ctAmps(next), amps, exports.STEADY_STATE_TOLERANCE)) {
            continue;
        }
        steadyPackets.push(current);
    }
    const runningCount = ctPackets.filter((p) => ctAmps(p) > MIN_RUNNING_CT_AMPS).length;
    if (steadyPackets.length < MIN_STEADY_SAMPLES) {
        console.warn(`No steady-state blower run found (${runningCount} running CT samples, ${steadyPackets.length} steady)`);
        return null;
    }
    // Step 2 — same operating point as the baseline.
    const referenceCT = options.referenceCT != null && options.referenceCT > MIN_RUNNING_CT_AMPS
        ? options.referenceCT
        : null;
    const operatingPoint = referenceCT ?? median(steadyPackets.map(ctAmps));
    const inBandPackets = steadyPackets.filter((p) => withinBand(ctAmps(p), operatingPoint, exports.OPERATING_POINT_TOLERANCE));
    if (inBandPackets.length < MIN_STEADY_SAMPLES) {
        console.warn(referenceCT != null
            ? `Blower did not run at the baseline operating point (${referenceCT} A) this window: ${steadyPackets.length} steady samples, ${inBandPackets.length} in band`
            : `Too few samples at the modal operating point (${operatingPoint} A): ${inBandPackets.length}`);
        return null;
    }
    const windowPackets = inBandPackets.slice(-STABLE_WINDOW_SIZE);
    console.log(`Steady window: ${windowPackets.length} of ${inBandPackets.length} in-band samples ` +
        `(${steadyPackets.length} steady / ${runningCount} running) at ${operatingPoint.toFixed(2)} A` +
        `${referenceCT != null ? ' [baseline operating point]' : ' [modal — baseline has no CT]'}`);
    const returnPackets = parsedPackets
        .filter((packet) => packet.packetType === 'R' && packet.pressure !== undefined)
        .sort((a, b) => a.timestamp - b.timestamp);
    const supplyPackets = parsedPackets
        .filter((packet) => packet.packetType === 'S' && packet.pressure !== undefined)
        .sort((a, b) => a.timestamp - b.timestamp);
    console.log(`Found ${returnPackets.length} R packets with pressure, ${supplyPackets.length} S packets with pressure`);
    if (returnPackets.length === 0 || supplyPackets.length === 0) {
        console.warn('Insufficient Return or Supply packets found');
        const allReturn = parsedPackets.filter((p) => p.packetType === 'R');
        const allSupply = parsedPackets.filter((p) => p.packetType === 'S');
        console.warn(`Total R packets: ${allReturn.length} (${returnPackets.length} with pressure), S packets: ${allSupply.length} (${supplyPackets.length} with pressure)`);
        if (allReturn.length > 0)
            console.warn(`Sample R packet: ${JSON.stringify(allReturn[0])}`);
        if (allSupply.length > 0)
            console.warn(`Sample S packet: ${JSON.stringify(allSupply[0])}`);
        return null;
    }
    // Step 3 — pair each steady CT sample with its R and S packets from the same moment and
    // compute TESP (Total External Static Pressure = |return| + supply). Median the valid
    // samples to shrug off single-reading noise.
    const samples = windowPackets
        .map((ct) => {
        const r = findClosestPacket(returnPackets, ct.timestamp);
        const s = findClosestPacket(supplyPackets, ct.timestamp);
        if (!r || !s)
            return null;
        const returnPressure = r.pressure;
        const supplyPressure = s.pressure;
        return {
            ct,
            returnPressure,
            supplyPressure,
            tesp: Math.abs(returnPressure) + supplyPressure,
        };
    })
        .filter((sample) => sample !== null)
        .filter(({ returnPressure, supplyPressure, tesp }) => {
        if (Math.abs(returnPressure) > MAX_VALID_PER_SIDE_PRESSURE ||
            Math.abs(supplyPressure) > MAX_VALID_PER_SIDE_PRESSURE ||
            tesp > MAX_VALID_TESP) {
            console.warn(`Dropping fault sample: return=${returnPressure}, supply=${supplyPressure}, tesp=${tesp}`);
            return false;
        }
        return true;
    });
    if (samples.length < MIN_STEADY_SAMPLES) {
        console.warn(`Only ${samples.length} steady samples had paired R/S pressures within ${MAX_PAIRING_GAP_SECONDS}s — skipping`);
        return null;
    }
    const pressureDelta = median(samples.map((s) => s.tesp));
    const operatingPointCT = median(samples.map((s) => ctAmps(s.ct)));
    // Surface the most recent sample's individual pressures for continuity in downstream
    // logs and the persisted peakLoadData JSON. pressureDelta is the median TESP.
    const latestSample = samples[samples.length - 1];
    const { returnPressure, supplyPressure } = latestSample;
    console.log(`Steady-state TESP median=${pressureDelta} at ${operatingPointCT} A (from ${samples.length} samples); latest R=${returnPressure} S=${supplyPressure}`);
    return {
        peakTimestamp: latestSample.ct.timestamp,
        peakCTValue: operatingPointCT,
        operatingPointCT,
        sampleCount: samples.length,
        returnPressure,
        supplyPressure,
        pressureDelta,
    };
}
exports.calculateHVACPeakLoadPressureDelta = calculateHVACPeakLoadPressureDelta;
function getReplacementDeltaForMerv(mervRating) {
    const m = parseInt(mervRating ?? '', 10);
    if (Number.isNaN(m))
        return DELTA_MERV_MID;
    if (m <= 8)
        return DELTA_MERV_LOW;
    if (m <= 12)
        return DELTA_MERV_MID;
    return DELTA_MERV_HIGH;
}
exports.getReplacementDeltaForMerv = getReplacementDeltaForMerv;
function calculateFilterHealthFromPressureDelta(pressureDelta, targetStaticPressure = DEFAULT_TARGET_STATIC_PRESSURE, startingStaticPressure = DEFAULT_STARTING_STATIC_PRESSURE) {
    console.log(`Calculating filter health from pressure delta: ${pressureDelta}, target: ${targetStaticPressure}, starting: ${startingStaticPressure}`);
    const pressureRange = targetStaticPressure - startingStaticPressure;
    // If pressure delta is at or below starting pressure, filter is like new (100% health)
    if (pressureDelta <= startingStaticPressure) {
        console.log(`Filter health percentage calculated: ${MAX_HEALTH_PERCENTAGE}% (at or below starting pressure)`);
        return MAX_HEALTH_PERCENTAGE;
    }
    // If pressure delta exceeds target, system is over limit (0% health - red alert)
    if (pressureDelta > targetStaticPressure) {
        console.log(`Filter health percentage calculated: ${MIN_HEALTH_PERCENTAGE}% (over target pressure - red alert)`);
        return MIN_HEALTH_PERCENTAGE;
    }
    const currentIncrease = pressureDelta - startingStaticPressure;
    const remainingRange = pressureRange - currentIncrease;
    const healthPercentage = (remainingRange / pressureRange) * MAX_HEALTH_PERCENTAGE;
    const finalHealth = Math.max(MIN_HEALTH_PERCENTAGE, Math.min(MAX_HEALTH_PERCENTAGE, Math.round(healthPercentage)));
    console.log(`Filter health calculation: pressureRange=${pressureRange}, currentIncrease=${currentIncrease}, ` +
        `remainingRange=${remainingRange}, healthPercentage=${healthPercentage.toFixed(1)}%, final=${finalHealth}%`);
    return finalHealth;
}
exports.calculateFilterHealthFromPressureDelta = calculateFilterHealthFromPressureDelta;
